import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getPlainTextFromSelection, replaceTextSelection } from '@lyearn/lygo-blocks';
import { EditorView } from '@lyearn/lygo-core';
import { useCancelGenerativeTask } from '@/components/LyearnAI/hooks/useCancelGenerativeTask';
import useGenerativeTask from '@/components/LyearnAI/hooks/useGenerativeTask';
import {
  GenerativeTaskStatusEnum,
  GenerativeTaskTypeEnum,
  TextTransformationEnum,
} from '@/types/schema';

export type Suggestion = {
  text: string;
  generativeTaskId: string;
};

const generativeTaskInput = (editorView: EditorView) => ({
  type: GenerativeTaskTypeEnum.TransformText,
  input: {
    transformText: {
      transformation: TextTransformationEnum.Rephrase,
      text: getPlainTextFromSelection(editorView),
    },
  },
});

export default function useQuestionRephrase({
  editorView,
  onClose,
}: {
  editorView: EditorView;
  onClose: () => void;
}) {
  const [typing, setTyping] = useState(false);
  const [suggestionData, setSuggestionData] = useState<Suggestion | null>(null);
  const refset = useRef<boolean>(false);

  const onExecuteGenerativeTask = useMemo(
    () => ({
      afterExecute: () => {
        setTyping(true);
      },
    }),
    [],
  );

  const onError = useCallback(() => {
    setTyping(false);
  }, []);

  const {
    executeCreateGenerativeTask,
    fetching,
    generativeTask,
    generativeTaskId,
    generativeTaskIdFromSubscription,
  } = useGenerativeTask({
    onExecuteGenerativeTask,
    onError,
  });

  const { onStopGenerating, fetching: isStopGenerating } =
    useCancelGenerativeTask(generativeTaskId);

  useEffect(() => {
    (async () => {
      if (refset.current) {
        return;
      }

      refset.current = true;
      await executeCreateGenerativeTask(generativeTaskInput(editorView));
    })();
  }, [editorView, executeCreateGenerativeTask]);

  const suggestionFromSubscription: Suggestion | null = useMemo(() => {
    const result = generativeTask?.result;
    const text = result?.transformText?.text;

    if (!text) {
      return null;
    }

    return {
      text,
      generativeTaskId: generativeTaskIdFromSubscription || generativeTaskId,
    };
  }, [generativeTask?.result, generativeTaskId, generativeTaskIdFromSubscription]);

  useEffect(() => {
    if (
      suggestionFromSubscription !== null &&
      generativeTask &&
      generativeTaskId === generativeTaskIdFromSubscription &&
      !fetching
    ) {
      setSuggestionData(suggestionFromSubscription);
      setTyping(
        [GenerativeTaskStatusEnum.Processing, GenerativeTaskStatusEnum.ToBeProcessed].includes(
          generativeTask.status,
        ),
      );
    }
  }, [
    fetching,
    generativeTask,
    generativeTaskId,
    generativeTaskIdFromSubscription,
    suggestionData,
    suggestionFromSubscription,
  ]);

  const replaceQuestion = useCallback(() => {
    replaceTextSelection(editorView, suggestionData?.text || '', true);
    onClose();
  }, [editorView, onClose, suggestionData?.text]);

  const regenerateQuestion = useCallback(async () => {
    await executeCreateGenerativeTask(generativeTaskInput(editorView));
  }, [editorView, executeCreateGenerativeTask]);

  return {
    // state
    suggestion: suggestionData,
    fetching,
    typing,
    generativeTaskId,

    // methods
    replaceQuestion,
    regenerateQuestion,
    stopGnerationCallback: onStopGenerating,
    isStopGenerating,
  };
}
